/**
 * BillingAbuseDashboard (iter83) — Admin surface that turns defensive
 * telemetry into growth intelligence.
 *
 * Sections:
 *   1. Headline tiles (429 count, failed vs successful auto-topups, revenue)
 *   2. Rate-limit abuse leaderboard (users hitting billing 429s)
 *   3. Failed auto-topups by reason + top affected users
 *   4. Top spenders (paid revenue in the window)
 *
 * Fetches everything in a single GET /api/admin/billing-abuse/dashboard.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldAlert, TrendingUp, XCircle, CheckCircle2, DollarSign,
  RefreshCw, AlertTriangle, Crown, Zap, Loader2, Mail, Send, History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import RecoveryROIPanel from './RecoveryROIPanel';
import logger from '@/lib/logger';

const REASON_LABELS = {
  card_error: { label: 'Card declined', color: 'bg-rose-900/40 text-rose-200' },
  stripe_error: { label: 'Stripe API error', color: 'bg-rose-900/40 text-rose-200' },
  no_payment_method: { label: 'No card on file', color: 'bg-amber-900/40 text-amber-200' },
  no_customer: { label: 'No Stripe customer', color: 'bg-amber-900/40 text-amber-200' },
  requires_action: { label: 'SCA required', color: 'bg-blue-900/40 text-blue-200' },
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return iso; }
};

const formatCents = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const HeadlineTile = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${accent || 'text-slate-100'}`} data-testid={testId}>
            {value}
          </p>
          {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
        </div>
        <Icon className={`h-5 w-5 ${accent || 'text-slate-500'}`} />
      </div>
    </CardContent>
  </Card>
);

const BillingAbuseDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window_, setWindow] = useState('7d');
  const [recoveryTarget, setRecoveryTarget] = useState(null); // {user_id, email, reason, count}
  const [recoveryHistory, setRecoveryHistory] = useState([]);
  const [recoverySending, setRecoverySending] = useState(false);
  const [recoveryForce, setRecoveryForce] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/billing-abuse/dashboard', { params: { window: window_ } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load billing abuse dashboard');
    } finally {
      setLoading(false);
    }
  }, [window_]);

  useEffect(() => { load(); }, [load]);

  const openRecovery = async (row) => {
    setRecoveryForce(false);
    setRecoveryTarget(row);
    setRecoveryHistory([]);
    try {
      const r = await api.get(`/admin/billing-abuse/recovery-email-history/${row.user_id}`);
      setRecoveryHistory(r.data?.rows || []);
    } catch (e) {
      // Non-fatal — dialog still opens
      logger.debug('recovery history fetch failed:', e);
    }
  };

  const sendRecovery = async () => {
    if (!recoveryTarget) return;
    setRecoverySending(true);
    try {
      const r = await api.post(
        `/admin/billing-abuse/send-recovery-email/${recoveryTarget.user_id}`,
        { reason: recoveryTarget.reason, force: recoveryForce },
      );
      if (r.data?.status === 'sent') {
        toast.success(`Recovery email sent to ${r.data.email}`);
        setRecoveryTarget(null);
        load();
      } else {
        toast.warning(`Email dispatch reported '${r.data?.status}' — check the SendGrid dashboard.`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Failed to send recovery email';
      if (status === 409) {
        toast.error(detail, { duration: 7000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setRecoverySending(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[300px]" data-testid="billing-abuse-loading">
        <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
      </div>
    );
  }

  const totals = data?.totals || {};
  const rateLimitHits = data?.rate_limit_hits || [];
  const failedByReason = data?.failed_topups_by_reason || [];
  const failedTopUsers = data?.failed_topups_top_users || [];
  const topSpenders = data?.top_spenders || [];

  return (
    <div className="p-8 space-y-6" data-testid="billing-abuse-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-1 flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-rose-400" />
            Billing Intelligence
          </h1>
          <p className="text-slate-400 text-sm">
            Defensive telemetry meets growth signal. Spot abuse, salvage failed charges, celebrate top spenders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={window_} onValueChange={setWindow}>
            <SelectTrigger className="w-32" data-testid="billing-abuse-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="billing-abuse-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeadlineTile
          icon={ShieldAlert} label="429 events" value={totals.rate_limit_429s ?? 0}
          sub="Billing endpoints throttled"
          accent={totals.rate_limit_429s > 0 ? 'text-rose-400' : 'text-slate-100'}
          testId="tile-429s"
        />
        <HeadlineTile
          icon={XCircle} label="Failed auto-topups" value={totals.failed_auto_topups ?? 0}
          sub="Recoverable revenue signal"
          accent={totals.failed_auto_topups > 0 ? 'text-amber-400' : 'text-slate-100'}
          testId="tile-failed-topups"
        />
        <HeadlineTile
          icon={CheckCircle2} label="Successful auto-topups" value={totals.successful_auto_topups ?? 0}
          sub="Silent revenue"
          accent="text-emerald-400"
          testId="tile-successful-topups"
        />
        <HeadlineTile
          icon={DollarSign} label="Credit-pack revenue" value={formatCents(totals.revenue_cents)}
          sub={`${totals.revenue_txn_count ?? 0} paid txns`}
          accent="text-emerald-400"
          testId="tile-revenue"
        />
      </div>

      {/* iter85 — Recovery ROI panel */}
      <RecoveryROIPanel window={window_} refreshKey={data?.generated_at} />

      {/* Rate-limit abuse leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <AlertTriangle className="h-5 w-5 text-rose-400" />
            Rate-limit abuse — Top offenders
          </CardTitle>
          <CardDescription>
            Users hitting the billing endpoint 429 cap repeatedly. Multiple IPs on a single user = likely automation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rateLimitHits.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-6" data-testid="rate-limit-hits-empty">
              No 429s in this window. Your rate limits are working.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">429 hits</TableHead>
                  <TableHead className="text-right">Distinct IPs</TableHead>
                  <TableHead className="text-right">Last hit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rateLimitHits.map((r) => (
                  <TableRow key={`${r.user_id || 'anon'}-${r.last_hit_at}`} data-testid={`rate-limit-row-${r.user_id || 'anon'}`}>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-100">
                        {r.email || <span className="text-slate-500 italic">Anonymous / unauth</span>}
                      </div>
                      {r.full_name && (
                        <div className="text-[10px] text-slate-500">{r.full_name}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className={r.hit_count > 20 ? 'bg-rose-900/40 text-rose-200' : 'bg-amber-900/40 text-amber-200'}>
                        {r.hit_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">{r.distinct_ips}</TableCell>
                    <TableCell className="text-right text-slate-400 text-xs">{formatRelative(r.last_hit_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Failed auto-topups */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <XCircle className="h-5 w-5 text-amber-400" />
            Failed auto-topups
          </CardTitle>
          <CardDescription>
            Revenue you almost captured. Reach out to no-card users with a &quot;Add a card in 30 sec&quot; email;
            for card_declined, prompt the user to update payment method.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* By-reason breakdown */}
          <div className="flex flex-wrap gap-2">
            {failedByReason.length === 0 ? (
              <p className="text-sm text-slate-500 italic" data-testid="failed-topups-empty">
                No failed auto-topups in this window — all charges succeeded.
              </p>
            ) : (
              failedByReason.map((r) => {
                const cfg = REASON_LABELS[r.reason] || { label: r.reason, color: 'bg-slate-800 text-slate-300' };
                return (
                  <Badge
                    key={r.reason}
                    className={`${cfg.color} text-xs px-3 py-1`}
                    data-testid={`failed-reason-${r.reason}`}
                  >
                    {cfg.label} — <strong className="ml-1">{r.count}</strong>
                  </Badge>
                );
              })
            )}
          </div>

          {/* Top affected users */}
          {failedTopUsers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead>Last message</TableHead>
                  <TableHead className="text-right">Last at</TableHead>
                  <TableHead className="text-right">Recover</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedTopUsers.map((r, i) => {
                  const cfg = REASON_LABELS[r.reason] || { label: r.reason, color: 'bg-slate-800 text-slate-300' };
                  return (
                    <TableRow
                      key={`${r.user_id}-${r.reason}-${i}`}
                      data-testid={`failed-topup-row-${r.user_id}-${r.reason}`}
                    >
                      <TableCell>
                        <div className="text-sm font-medium text-slate-100">
                          {r.email || <span className="text-slate-500 italic">Unknown</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-slate-100">{r.count}</TableCell>
                      <TableCell className="text-xs text-slate-400 max-w-xs truncate">
                        {r.last_message || '—'}
                      </TableCell>
                      <TableCell className="text-right text-slate-400 text-xs">{formatRelative(r.last_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-rose-800/50 text-rose-200 hover:bg-rose-950/40"
                          onClick={() => openRecovery(r)}
                          disabled={!r.email}
                          data-testid={`recovery-btn-${r.user_id}-${r.reason}`}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          Recover
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Top spenders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Crown className="h-5 w-5 text-amber-400" />
            Top spenders (credit packs)
          </CardTitle>
          <CardDescription>
            Users driving the most credit-pack revenue this window. Auto-topup users flagged with a bolt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topSpenders.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-6" data-testid="top-spenders-empty">
              No paid credit-pack transactions in this window.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Total spent</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-right">Purchases</TableHead>
                  <TableHead className="text-right">Auto</TableHead>
                  <TableHead className="text-right">Last txn</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topSpenders.map((r, i) => (
                  <TableRow key={`${r.user_id}-${i}`} data-testid={`top-spender-row-${r.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {i < 3 && <Crown className="h-3.5 w-3.5 text-amber-400" />}
                        <div>
                          <div className="text-sm font-medium text-slate-100">
                            {r.email || <span className="text-slate-500 italic">Unknown</span>}
                          </div>
                          {r.full_name && (
                            <div className="text-[10px] text-slate-500">{r.full_name}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-emerald-400 font-semibold">{formatCents(r.total_cents)}</span>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">{r.total_credits?.toLocaleString?.() ?? r.total_credits}</TableCell>
                    <TableCell className="text-right text-slate-300">{r.txn_count}</TableCell>
                    <TableCell className="text-right">
                      {r.auto_topup_count > 0 ? (
                        <Badge className="bg-rose-950/40 text-rose-200 text-[10px]">
                          <Zap className="h-2.5 w-2.5 mr-0.5" /> {r.auto_topup_count}
                        </Badge>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-slate-400 text-xs">{formatRelative(r.last_txn_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-500 text-right">
        Generated at {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} · window: {window_}
        {typeof data?.recovery_emails_sent === 'number' && (
          <span className="ml-3 text-emerald-400">
            · {data.recovery_emails_sent} recovery email{data.recovery_emails_sent === 1 ? '' : 's'} sent in window
          </span>
        )}
      </p>

      {/* iter84 — Recovery email dialog */}
      <Dialog open={!!recoveryTarget} onOpenChange={(open) => { if (!open) setRecoveryTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="recovery-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-rose-400" />
              Send recovery email
            </DialogTitle>
            <DialogDescription>
              Dispatches a reason-specific SendGrid email with a deep link back into their subscription page.
            </DialogDescription>
          </DialogHeader>

          {recoveryTarget && (
            <div className="space-y-3 text-sm">
              <div className="border border-[#1F2A4D] bg-[#0F1730]/60 rounded-md p-3">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">To</div>
                <div className="text-slate-100 font-medium">{recoveryTarget.email}</div>
                <div className="text-[11px] text-slate-500 mt-2 uppercase tracking-wider mb-1">Reason</div>
                <Badge className={`${(REASON_LABELS[recoveryTarget.reason] || {}).color || 'bg-slate-800 text-slate-300'} text-[10px]`}>
                  {(REASON_LABELS[recoveryTarget.reason] || {}).label || recoveryTarget.reason}
                </Badge>
                <span className="text-slate-500 text-[11px] ml-3">
                  {recoveryTarget.count} recent failure{recoveryTarget.count === 1 ? '' : 's'}
                </span>
              </div>

              {/* Recent history */}
              {recoveryHistory.length > 0 && (
                <div className="border border-amber-800/40 bg-amber-950/20 rounded-md p-3">
                  <div className="text-[11px] font-semibold text-amber-200 flex items-center gap-1 mb-2">
                    <History className="h-3 w-3" />
                    Recent recovery emails to this user
                  </div>
                  <div className="space-y-1 text-[11px] text-amber-300/90 max-h-24 overflow-y-auto">
                    {recoveryHistory.slice(0, 5).map((h, i) => (
                      <div key={`${h.ts}-${i}`} className="flex items-center justify-between gap-2">
                        <span className="truncate">{(REASON_LABELS[h.reason] || {}).label || h.reason}</span>
                        <span className="text-amber-500/60 shrink-0">{formatRelative(h.ts)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Force override */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Label htmlFor="recovery-force" className="text-xs text-slate-400 cursor-pointer">
                  Override 24-hour cooldown
                </Label>
                <Switch
                  id="recovery-force"
                  checked={recoveryForce}
                  onCheckedChange={setRecoveryForce}
                  data-testid="recovery-force-toggle"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoveryTarget(null)}>Cancel</Button>
            <Button
              onClick={sendRecovery}
              disabled={recoverySending}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="recovery-send-btn"
            >
              {recoverySending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send email</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingAbuseDashboard;
